import type { Account, BetSettings, UserSettings } from '@/api/auth';
import {
  DEFAULT_MAX_COMBI,
  DEFAULT_MAX_SYSTEM,
  DEFAULT_MIN_SYSTEM,
  normalizeSystemLimits,
  type BetslipSelectionDraft,
  type BetslipSystemLimits,
  type BetslipToggleOptions,
} from '@/domain/betslip';
import type { EventOutcome } from '@/ui/sports/EventRow';

/** Build a betslip draft from a list/detail event DTO + clicked outcome. */
export function buildBetslipDraft(
  event: {
    EventId: number;
    Home?: string | null;
    Away?: string | null;
    HomeScore?: number | null;
    AwayScore?: number | null;
    Date?: string | null;
    Live?: boolean;
    Category?: string | null;
    Tournament?: string | null;
    TournamentId?: number | null;
    Sport?: string | null;
    SportId?: number;
  },
  outcome: EventOutcome,
  options?: {
    outright?: boolean;
    feedType?: number;
    seasonId?: number;
    seasonName?: string;
  },
): BetslipSelectionDraft | null {
  const marketId = outcome.marketId ?? outcome.id.split(':')[0];
  const oddValue = outcome.oddValue;
  if (!marketId || oddValue === undefined || !(oddValue > 0)) return null;

  const outcomeCode = outcome.outcomeCode ?? outcome.outcome;

  return {
    eventId: event.EventId,
    home: event.Home ?? 'Home',
    away: event.Away ?? 'Away',
    homeScore:
      event.HomeScore !== null && event.HomeScore !== undefined
        ? String(event.HomeScore)
        : undefined,
    awayScore:
      event.AwayScore !== null && event.AwayScore !== undefined
        ? String(event.AwayScore)
        : undefined,
    date: event.Date ?? undefined,
    live: event.Live,
    outright: options?.outright ?? false,
    seasonId: options?.seasonId,
    seasonName: options?.seasonName,
    category: event.Category ?? undefined,
    tournament: event.Tournament ?? undefined,
    tournamentId: event.TournamentId ?? undefined,
    sport: event.Sport ?? undefined,
    sportId: event.SportId,
    marketId,
    market: outcome.marketName ?? marketId,
    specifier: outcome.specifier ?? null,
    outcome: outcomeCode,
    outcomeId: outcomeCode,
    outcomeName: outcome.outcomeName ?? outcome.outcome,
    odd: oddValue,
    feedType: options?.feedType,
  };
}

function readBetSettings(
  user?: UserSettings | null,
  account?: Account | null,
): BetSettings | undefined {
  const fromUser = user?.BetSettings;
  if (fromUser && typeof fromUser === 'object') return fromUser;
  const fromAccount = account?.BetSettings;
  if (fromAccount && typeof fromAccount === 'object') return fromAccount;
  return undefined;
}

/** Resolve MinimumSystem / MaximumSystem from user (preferred) or account BetSettings. */
export function resolveSystemLimitsFromSettings(
  user?: UserSettings | null,
  account?: Account | null,
): BetslipSystemLimits {
  const betSett = readBetSettings(user, account);
  return normalizeSystemLimits({
    minSystem:
      typeof betSett?.MinimumSystem === 'number'
        ? betSett.MinimumSystem
        : DEFAULT_MIN_SYSTEM,
    maxSystem:
      typeof betSett?.MaximumSystem === 'number'
        ? betSett.MaximumSystem
        : DEFAULT_MAX_SYSTEM,
  });
}

export function betslipToggleOptionsFromAccount(
  account: Account | null | undefined,
  user?: UserSettings | null,
): BetslipToggleOptions {
  const disableMultiCombo = Boolean(account?.DisableMultiComboTicket);
  const betSett = readBetSettings(user, account);
  const maxRaw = betSett?.MaximumCombi ?? account?.MaximumCombi;
  const maxCombi =
    typeof maxRaw === 'number' && Number.isFinite(maxRaw) && maxRaw > 0
      ? maxRaw
      : DEFAULT_MAX_COMBI;
  const systemLimits = resolveSystemLimitsFromSettings(user, account);
  return {
    disableMultiCombo,
    maxCombi,
    minSystem: systemLimits.minSystem,
    maxSystem: systemLimits.maxSystem,
  };
}
